import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppButton,
  AppButtonRow,
  AppLoadingState,
  AppPage,
  AppPageIntro,
  AppText,
} from '../../ui-foundation';

const NATIVE_STATE_PREFIX = 'mobile-';
const NATIVE_CALLBACK_URL = 'adulting:///money/open-banking-callback';

function buildNativeCallbackUrl(searchParams: URLSearchParams) {
  const callbackParams = new URLSearchParams();

  for (const key of ['code', 'state', 'error', 'error_description', 'scope']) {
    const value = searchParams.get(key);
    if (value) {
      callbackParams.set(key, value);
    }
  }

  const query = callbackParams.toString();
  return query ? `${NATIVE_CALLBACK_URL}?${query}` : NATIVE_CALLBACK_URL;
}

export default function OpenBankingMobileCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showFallbackHint, setShowFallbackHint] = useState(false);
  const state = searchParams.get('state');
  const isNativeCallback = Boolean(state?.startsWith(NATIVE_STATE_PREFIX));
  const callbackUrl = useMemo(() => buildNativeCallbackUrl(searchParams), [searchParams]);

  useEffect(() => {
    if (!isNativeCallback) {
      navigate('/', { replace: true });
      return;
    }

    const redirect = window.setTimeout(() => {
      window.location.replace(callbackUrl);
    }, 250);

    const fallbackHint = window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        setShowFallbackHint(true);
      }
    }, 1800);

    return () => {
      window.clearTimeout(redirect);
      window.clearTimeout(fallbackHint);
    };
  }, [callbackUrl, isNativeCallback, navigate]);

  if (!isNativeCallback) {
    return null;
  }

  return (
    <AppPage gap="$lg" maxWidth={640} mx="auto" py="$xl">
      <AppPageIntro
        title="Returning to Adulting"
        subtitle="We’re handing your secure bank connection back into the app now."
      />
      <AppLoadingState
        title="Finishing secure bank connection"
        body="Adulting should reopen now. Once it does, keep the app in front for a few seconds while your accounts and recent transactions finish importing."
        action={(
          <AppButtonRow>
            <AppButton onPress={() => window.location.replace(callbackUrl)}>
              Open Adulting app
            </AppButton>
          </AppButtonRow>
        )}
      />
      {showFallbackHint ? (
        <AppText color="$colorMuted" textAlign="center">
          If Adulting is already open again, keep that screen in front. The connection may take a few seconds to finish before the confirmation prompt appears.
        </AppText>
      ) : null}
    </AppPage>
  );
}
